import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import { generateGuid } from 'utils/guid';
import { normalizeSlugInput } from 'utils/slug';
import { z } from 'zod';
import type { AimIntegrationDraft } from '../../../model/aimDrafts';
import { nullableTextInput } from './aimFormHelpers';
import { useTestAimIntegrationConnection } from './useTestAimIntegrationConnection';

const platformOptions = [{ value: 'Illumio' as const, label: 'Illumio' }];

const aimIntegrationBaseSchema = z.object({
  platform: z.literal('Illumio'),
  apiUrl: z
    .string()
    .min(1, 'URL is required')
    .refine(
      (value) => value.startsWith('https://'),
      'Url has to start with https://',
    ),
  apiKey: z.string().min(1, 'Key is required'),
  apiSecret: z.string(),
  organizationId: z.string().min(1, 'Organization ID is required'),
  slug: z
    .string()
    .transform(normalizeSlugInput)
    .pipe(
      z
        .string()
        .min(1, 'Slug is required')
        .max(64, 'Slug must be at most 64 characters')
        .regex(
          /^[a-zA-Z0-9_-]*$/,
          'Field fails to match the required pattern: /^[a-zA-Z0-9_-]*$/',
        ),
    ),
  comment: nullableTextInput,
});

// apiSecret is only required when there is no existing secret to fall back to
// (add mode, or edit mode without a previously stored secret).
// existingSlugs should already exclude the integration being edited, so
// saving without changing its own slug doesn't trigger a false duplicate.
export const getAimIntegrationFormSchema = (
  formType: 'add' | 'edit',
  hasExistingApiSecret: boolean,
  existingSlugs: string[] = [],
) =>
  aimIntegrationBaseSchema
    .refine(
      (data) =>
        !!data.apiSecret || (formType === 'edit' && hasExistingApiSecret),
      { message: 'Secret is required', path: ['apiSecret'] },
    )
    .refine((data) => !existingSlugs.includes(data.slug), {
      message: 'Slug must be unique among existing integrations',
      path: ['slug'],
    });

type AimIntegrationFormInputValues = {
  platform: 'Illumio';
  apiUrl: string;
  apiKey: string;
  apiSecret: string;
  organizationId: string;
  slug: string;
  comment: string;
};

type AimIntegrationFormOutputValues = z.output<
  ReturnType<typeof getAimIntegrationFormSchema>
>;

export const toAimIntegrationFormValues = (
  row?: AimIntegrationDraft,
): AimIntegrationFormInputValues => ({
  platform: 'Illumio',
  apiUrl: row?.apiUrl ?? '',
  apiKey: row?.apiKey ?? '',
  apiSecret: '',
  organizationId: row?.organizationId ?? '',
  slug: row?.slug ?? '',
  comment: row?.comment ?? '',
});

type Props = {
  formType: 'add' | 'edit';
  initialValues?: AimIntegrationDraft;
  hasExistingApiSecret?: boolean;
  existingIntegrations: AimIntegrationDraft[];
  onClose: () => void;
  onSubmit: (entity: AimIntegrationDraft) => void;
};

export const AimIntegrationDialog = ({
  formType,
  initialValues,
  hasExistingApiSecret = false,
  existingIntegrations,
  onClose,
  onSubmit,
}: Props) => {
  const existingSlugs = existingIntegrations
    .filter((integration) => integration.id !== initialValues?.id)
    .map((integration) => integration.slug);

  const form = useCustomForm<
    AimIntegrationFormInputValues,
    unknown,
    AimIntegrationFormOutputValues
  >({
    defaultValues: toAimIntegrationFormValues(initialValues),
    resolver: zodResolver(
      getAimIntegrationFormSchema(
        formType,
        hasExistingApiSecret,
        existingSlugs,
      ),
    ),
  });

  const { isTesting, testConnection } = useTestAimIntegrationConnection();

  const handleSave = form.handleSubmit((values) => {
    const _id =
      formType === 'edit' && initialValues ? initialValues.id : generateGuid();

    const entity: AimIntegrationDraft = {
      id: _id,
      platform: 'Illumio',
      apiUrl: values.apiUrl,
      apiKey: values.apiKey,
      // Blank means "keep the existing secret" when editing.
      apiSecret: values.apiSecret || (initialValues?.apiSecret ?? ''),
      organizationId: values.organizationId,
      slug: values.slug,
      comment: values.comment || null,
      // Stays true until the settings form is saved and the drafts are rebuilt
      // from the API response; see AimIntegrationDraft.unsaved.
      unsaved: formType === 'add' ? true : initialValues?.unsaved,
    };
    onSubmit(entity);
    onClose();
  });

  // Validate first so the user sees the same field errors saving would show,
  // instead of a request that is bound to fail.
  const handleTestConnection = form.handleSubmit((values) =>
    testConnection({ values, initialValues }),
  );

  return (
    <SidePanel
      title={
        formType === 'add'
          ? 'Add integration provider'
          : 'Edit integration provider'
      }
      closeOnBackdropClick={false}
      onClose={onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            disabled={!form.formState.isDirty || isTesting}
            // Both buttons submit the form, so isSubmitting is also true while
            // testing — only the button that was pressed should spin.
            loading={form.formState.isSubmitting && !isTesting}
            onClick={handleSave}
          >
            {formType === 'add' ? 'Add provider' : 'Save changes'}
          </Button>
          <Button
            className='flex-1'
            variant='outline'
            type='button'
            loading={isTesting}
            onClick={handleTestConnection}
          >
            Test connection
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <FormSelect.Mandatory
            control={form.control}
            name='platform'
            label='Platform'
            description='Choose one of the supported platforms'
            options={platformOptions}
            disabled
          />
          <FormTextField
            control={form.control}
            name='apiUrl'
            label='API URL'
            description='PCE base URL (host + port)'
            type='text'
          />
          <FormTextField
            control={form.control}
            name='apiKey'
            label='API key'
            description='Illumio authentication username'
            type='text'
          />
          <FormTextField
            control={form.control}
            name='apiSecret'
            label='API secret'
            type='password'
            description={
              formType === 'edit' && hasExistingApiSecret
                ? 'Leave blank to keep the current secret'
                : undefined
            }
          />
          <FormTextField
            control={form.control}
            name='organizationId'
            label='Organization ID'
            type='text'
          />
          <FormTextField
            control={form.control}
            name='slug'
            label='Slug'
            type='text'
            labelTooltip='Slug is a unique descriptive identifier of the configuration instance. Use short descriptive value, like illumio-eu.'
          />
          <FormTextArea
            control={form.control}
            name='comment'
            label='Comment (optional)'
          />
        </Card>
      </form>
    </SidePanel>
  );
};
